import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { APP_PERMISSIONS } from '../constants/appRoles';

const UserRoleContext = createContext({
  role: null,
  roleDisplayName: null,
  email: null,
  name: null,
  permissions: {},
  isLoaded: false,
  canAccessConfiguration: false,
});

export function UserRoleProvider({ children }) {
  const { authFetch, isReady } = useAuthFetch();
  const [profile, setProfile] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!isReady) {
      return undefined;
    }

    let cancelled = false;

    const loadProfile = async () => {
      try {
        const response = await authFetch(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/auth/me`
        );

        if (cancelled) {
          return;
        }

        if (response.ok) {
          const json = await response.json();
          setProfile(json?.data || null);
        } else {
          setProfile(null);
        }
      } catch (error) {
        console.error('Failed to load user role profile:', error);
        if (!cancelled) {
          setProfile(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoaded(true);
        }
      }
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [authFetch, isReady]);

  const value = useMemo(() => {
    const permissions = profile?.permissions || {};

    return {
      role: profile?.role || null,
      roleDisplayName: profile?.roleDisplayName || null,
      email: profile?.email || null,
      name: profile?.name || null,
      permissions,
      isLoaded,
      canAccessConfiguration: Boolean(permissions[APP_PERMISSIONS.ACCESS_CONFIGURATION]),
    };
  }, [profile, isLoaded]);

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
}

export function useAppRole() {
  return useContext(UserRoleContext);
}
